import { useEffect, useState } from 'react';
import type { StatsJSON, VersionStatistics } from '../About/types';
import { compareVersions } from '../About/helpers';
import { apiReleaseToVersionsArray } from '../About/releaseApiMappers';
import { releasesApiService } from '../../services/releasesApiService';

function pickPositiveCount(...candidates: (number | undefined)[]): number {
  for (const n of candidates) {
    if (n != null && n > 0) return n;
  }
  return 0;
}

function formatStat(num: number | undefined, suffix = '+'): string {
  const n = num ?? 0;
  if (n <= 0) return '0';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M${suffix}`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K${suffix}`;
  return `${n}${suffix}`;
}

export interface FormattedPricingStats {
  gene: string;
  variant: string;
  disease: string;
  gda_summary: string;
  vda_summary: string;
  dda: string;
  chemical: string;
}

export interface PricingStatsResult {
  formatted: FormattedPricingStats | null;
  academicFormatted: FormattedPricingStats;
  statsLine: string;
  academicStatsLine: string;
  loading: boolean;
}

const DEFAULT_STATS_LINE = '30,300+ genes · 1.6M+ variants · 38,100+ diseases';
const ACADEMIC_STATS_LINE = '14k+ genes · 950K+ variants · 18K+ diseases';

const DEFAULT_FORMATTED: FormattedPricingStats = {
  gene: '30,300+',
  variant: '1.6M+',
  disease: '38,100+',
  gda_summary: '2M+',
  vda_summary: '4.7M+',
  dda: '24.4M+',
  chemical: '12,000+',
};

export const ACADEMIC_FORMATTED: FormattedPricingStats = {
  gene: '14k+',
  variant: '950K+',
  disease: '18K+',
  gda_summary: '100k+',
  vda_summary: '1.4M+',
  dda: '308K+',
  chemical: '400+',
};

function formatFromStatistics(s: VersionStatistics): FormattedPricingStats {
  return {
    gene: formatStat(pickPositiveCount(s.gene, s.gda_gene)),
    variant: formatStat(pickPositiveCount(s.variant, s.vda_variant)),
    disease: formatStat(pickPositiveCount(s.disease, s.gda_disease)),
    gda_summary: formatStat(pickPositiveCount(s.gda_summary)),
    vda_summary: formatStat(pickPositiveCount(s.vda_summary)),
    dda: formatStat(pickPositiveCount(s.dda_summary, s.dda)),
    chemical: formatStat(pickPositiveCount(s.chemical, s.chemicals_gda_or_vda)),
  };
}

function buildStatsLine(formatted: FormattedPricingStats): string {
  return `${formatted.gene} genes · ${formatted.variant} variants · ${formatted.disease} diseases`;
}

export function usePricingStats(): PricingStatsResult {
  const [formatted, setFormatted] = useState<FormattedPricingStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function loadLatestStats() {
      try {
        let statsData: StatsJSON = { current: '' };
        try {
          const statsRes = await fetch('/stats.json');
          if (statsRes.ok) {
            statsData = (await statsRes.json()) as StatsJSON;
          }
        } catch {
          // Ignore and fallback to releases API
        }

        const statsVersions = Object.keys(statsData).filter(k => k !== 'current');
        const apiReleasesList = await releasesApiService.getReleasesList();
        const apiVersions = apiReleasesList.map(r => r.version);
        const allVersions = [...new Set([...statsVersions, ...apiVersions])];

        let stats: VersionStatistics | null = null;
        if (allVersions.length > 0) {
          const latestVersion = allVersions.sort((a, b) => compareVersions(b, a))[0];
          const versionData = statsData[latestVersion];
          const statsFromJson =
            typeof versionData === 'object' && versionData && 'statistics' in versionData
              ? versionData.statistics
              : null;

          if (statsFromJson) {
            stats = statsFromJson;
          } else {
            const latestRelease = await releasesApiService.getRelease(latestVersion);
            const mapped = apiReleaseToVersionsArray(latestRelease);
            stats = mapped.statistics;
          }
        }

        if (!cancelled) {
          setFormatted(stats ? formatFromStatistics(stats) : DEFAULT_FORMATTED);
        }
      } catch {
        if (!cancelled) setFormatted(DEFAULT_FORMATTED);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadLatestStats();
    return () => {
      cancelled = true;
    };
  }, []);

  const effective = formatted ?? DEFAULT_FORMATTED;
  const statsLine = formatted ? buildStatsLine(formatted) : DEFAULT_STATS_LINE;

  return {
    formatted: effective,
    academicFormatted: ACADEMIC_FORMATTED,
    statsLine,
    academicStatsLine: ACADEMIC_STATS_LINE,
    loading,
  };
}
